/**
 * SSE streaming client for the telemetry push pipeline (GET-based, long-lived).
 *
 * WHY A SEPARATE MODULE FROM sseClient.ts:
 * sseClient.ts's openSSEStream is POST-based (a request body carries render
 * parameters) and short-lived (a dashboard render finishes in seconds) — it
 * fails once and leaves retry to the caller. This stream is GET-based (no
 * body, matching a plain resource fetch), long-lived (open for as long as a
 * facility page is mounted), and expected to drop and recover repeatedly
 * over that lifetime — so reconnect-with-backoff has to live inside the
 * transport itself, not be re-implemented by every caller. Auth handling
 * (getValidToken, 401-refresh-retry) and record parsing (parseSseBuffer) are
 * reused directly from sseClient.ts rather than duplicated.
 */

import { getValidToken, parseSseBuffer } from "./sseClient";
import { refreshAccessToken } from "./authRefresh";
import { API_BASE_URL } from "./env";

// "not_found" (JAA-1117): the facility no longer exists — e.g. it was
// permanently deleted. Terminal, like "forbidden": retrying can never succeed.
export type TelemetryStreamState = "live" | "reconnecting" | "forbidden" | "not_found";

export interface TelemetryStreamStateMeta {
  /** True when this reconnect follows a server-sent `event: bye` (a clean,
   *  scheduled close) rather than an unexpected drop. Callers use this to
   *  suppress "Reconnecting…" UI for the expected case. */
  expected?: boolean;
  /** Only set for state "forbidden": true when the 403 body identifies the
   *  backend's dedicated "facility deactivated" case specifically, same
   *  signature as isFacilityDeactivatedError (apiError.ts) — false for any
   *  other 403 (e.g. the user was never granted access to this facility). */
  deactivated?: boolean;
}

export interface TelemetryStreamOptions<TData> {
  /** Path relative to VITE_API_BASE_URL, e.g. "/api/v1/facilities/1/telemetry/stream" */
  url: string;
  /** Called for every parsed SSE record (e.g. "telemetry", "stale", "bye"). */
  onEvent: (eventType: string, data: TData) => void;
  /** Called on every state transition. `meta` is only populated for "reconnecting". */
  onStateChange: (state: TelemetryStreamState, meta?: TelemetryStreamStateMeta) => void;
}

const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;
const JITTER_RATIO = 0.2;

/** Capped exponential backoff, jittered so tabs reconnecting after the same
 * event (e.g. a replica restart) don't all retry in lockstep. */
function backoffDelay(attempt: number): number {
  const base = Math.min(INITIAL_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS);
  return base + base * JITTER_RATIO * Math.random();
}

/** Mirrors isFacilityDeactivatedError (apiError.ts), adapted for a fetch
 * Response instead of an axios error — true when a 403's JSON body
 * identifies this as the backend's dedicated "facility deactivated" case
 * rather than a generic access-denied 403. Never throws: an unparseable or
 * unexpected body just means "not identifiably the deactivated case". */
async function isDeactivatedResponse(response: Response): Promise<boolean> {
  try {
    const data: unknown = await response.json();
    if (typeof data !== "object" || data === null) return false;
    const d = data as Record<string, unknown>;
    const text = [d.detail, d.message].find((v) => typeof v === "string") as
      | string
      | undefined;
    return text?.toLowerCase().includes("deactivated") ?? false;
  } catch {
    return false;
  }
}

/** Shared helper so the 401-retry path can call it twice with different tokens. */
async function openStream(url: string, token: string, signal: AbortSignal): Promise<Response> {
  return fetch(`${API_BASE_URL}${url}`, {
    method: "GET",
    headers: {
      Accept: "text/event-stream",
      Authorization: `Bearer ${token}`,
    },
    signal,
  });
}

/**
 * Opens a long-lived GET SSE stream with automatic reconnect. Returns a
 * teardown function — call it on unmount or facility switch to abort the
 * in-flight connection and cancel any pending reconnect attempt.
 *
 * Reconnects indefinitely on failure (no fixed attempt count, never falls
 * back to polling) — how long to stay in "reconnecting" before a caller
 * escalates its own UI is a decision for the consuming hook, not this
 * transport.
 */
export function openTelemetryStream<TData>(
  options: TelemetryStreamOptions<TData>,
): () => void {
  const { url, onEvent, onStateChange } = options;

  let tornDown = false;
  let controller = new AbortController();
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  let attempt = 0;

  const scheduleReconnect = (expected: boolean) => {
    if (tornDown) return;
    onStateChange("reconnecting", { expected });
    const delay = backoffDelay(attempt);
    attempt += 1;
    reconnectTimer = setTimeout(() => {
      if (tornDown) return;
      controller = new AbortController();
      void connectOnce();
    }, delay);
  };

  const connectOnce = async () => {
    // Tracks whether this specific connection saw a "bye" before ending, so
    // a clean server-initiated close can be distinguished from a real drop.
    let sawBye = false;

    let token: string | null;
    try {
      token = await getValidToken();
    } catch {
      if (tornDown) return;
      scheduleReconnect(false);
      return;
    }
    if (tornDown) return;
    if (!token) {
      scheduleReconnect(false);
      return;
    }

    let response: Response;
    try {
      response = await openStream(url, token, controller.signal);
    } catch {
      if (tornDown || controller.signal.aborted) return;
      scheduleReconnect(false);
      return;
    }

    // 401 retry: token was valid when read but expired before the fetch
    // completed. Mirrors sseClient.ts's one-shot retry.
    if (response.status === 401) {
      const newToken = await refreshAccessToken();
      if (tornDown) return;
      if (!newToken) {
        scheduleReconnect(false);
        return;
      }
      try {
        response = await openStream(url, newToken, controller.signal);
      } catch {
        if (tornDown || controller.signal.aborted) return;
        scheduleReconnect(false);
        return;
      }
    }

    // 403 is terminal, not transient — the facility is either deactivated or
    // the user was never granted access, and retrying with backoff can never
    // resolve either case on its own (PR #323 review: this previously fell
    // through to scheduleReconnect like any other failure, retrying forever).
    if (response.status === 403) {
      if (tornDown) return;
      const deactivated = await isDeactivatedResponse(response);
      if (tornDown) return;
      onStateChange("forbidden", { deactivated });
      return;
    }

    // 404 is terminal too (JAA-1117): the facility was permanently deleted
    // (or never existed). Retrying with backoff would spin forever against
    // an endpoint that can't come back.
    if (response.status === 404) {
      if (tornDown) return;
      onStateChange("not_found");
      return;
    }

    if (!response.ok || !response.body) {
      if (tornDown) return;
      scheduleReconnect(false);
      return;
    }

    attempt = 0;
    onStateChange("live", undefined);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    // parseSseBuffer's "done" signal is specific to sseClient.ts's render-stream
    // use case; the telemetry stream never sends a "done" event, so this is
    // always a no-op here — passed through only because parseSseBuffer requires it.
    const doneFired = { value: false };

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        buffer = parseSseBuffer<TData>(
          buffer,
          (eventType, data) => {
            if (eventType === "bye") sawBye = true;
            onEvent(eventType, data);
          },
          doneFired,
          () => {},
        );
      }
    } catch {
      // AbortController.abort() causes reader.read() to reject — expected on teardown.
      if (tornDown || controller.signal.aborted) return;
      scheduleReconnect(sawBye);
      return;
    }

    // Reader loop ended without throwing — the server closed the connection
    // (scheduled "bye" close, or otherwise) rather than the network dropping.
    if (tornDown) return;
    scheduleReconnect(sawBye);
  };

  void connectOnce();

  return () => {
    tornDown = true;
    controller.abort();
    if (reconnectTimer) clearTimeout(reconnectTimer);
  };
}
